import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Users, BarChart3, MousePointerClick, Sparkles, RefreshCw } from "lucide-react";

export default function Founder() {
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/dashboard");
      setData(d);
      setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading]);

  if (authLoading || loading) {
    return (
      <main className="max-w-6xl mx-auto px-6 sm:px-8 py-16 text-center">
        <p style={{ color: "var(--text-2)" }}>Loading dashboard…</p>
      </main>
    );
  }
  if (err) {
    return (
      <main className="max-w-3xl mx-auto px-6 sm:px-8 py-16">
        <p className="label-eyebrow mb-2">Founder dashboard</p>
        <h1 className="font-serif text-3xl mb-3">{err}</h1>
        <p className="text-sm" style={{ color: "var(--text-2)" }}>
          You&apos;re signed in as {user?.email || "no one"}. Admin access requires the email configured in the
          backend <code>ADMIN_EMAIL</code> env var.
        </p>
      </main>
    );
  }
  if (!data) return null;

  const m = data.metrics;
  return (
    <main className="max-w-6xl mx-auto px-6 sm:px-8 py-10 sm:py-16" data-testid="founder-page">
      <header className="mb-10 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="label-eyebrow mb-2">Founder dashboard</p>
          <h1 className="font-serif text-4xl sm:text-5xl leading-tight">Alternavit · pulse</h1>
        </div>
        <button onClick={load} className="btn-ghost text-sm flex items-center gap-1.5" data-testid="founder-refresh">
          <RefreshCw className="w-4 h-4" strokeWidth={1.5} /> Refresh
        </button>
      </header>

      {/* Top metrics */}
      <section className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12" data-testid="founder-metrics">
        <Stat icon={Users} label="Total users" value={m.total_users} delta={`+${m.users_7d} this week`} />
        <Stat icon={BarChart3} label="Assessments" value={m.total_assessments} delta={`+${m.assessments_7d} this week`} />
        <Stat icon={MousePointerClick} label="Affiliate clicks" value={m.total_clicks} delta={`+${m.clicks_7d} this week`} />
        <Stat icon={Sparkles} label="CTR (clicks/assessment)" value={`${m.click_through_rate_pct}%`} delta={`${m.avg_assessments_per_user} avg per user`} />
      </section>

      {/* Section: Acquisition */}
      <Section title="Where they came from">
        <div className="grid sm:grid-cols-2 gap-6">
          <Bars
            title="Top referrer domains"
            items={data.top_referrers.map((r) => ({ label: r.domain, value: r.count }))}
            extra={[{ label: "(direct / typed in)", value: data.direct_users }]}
            empty="No referrer data yet — testers reach you directly."
          />
          <div className="space-y-6">
            <Bars
              title="UTM sources"
              items={data.top_utm_sources.map((r) => ({ label: r.name, value: r.count }))}
              empty="No UTM-tagged traffic yet. Tag your campaign URLs to see splits."
            />
            <Bars
              title="UTM campaigns"
              items={data.top_utm_campaigns.map((r) => ({ label: r.name, value: r.count }))}
              empty="Add ?utm_campaign=instagram-launch to track campaigns."
            />
          </div>
        </div>
      </Section>

      {/* Section: Who they are */}
      <Section title="Who they are">
        <div className="grid sm:grid-cols-2 gap-6">
          <SignupSparkline items={data.signups_by_day} />
          <Stats
            rows={[
              ["Users with at least one assessment", m.users_with_assessment],
              ["Active in tracker (last 7 days)", m.active_users_7d],
              ["Avg assessments per user", m.avg_assessments_per_user],
              ["Tracker entries (taken=true)", m.total_tracker_logs],
            ]}
          />
        </div>
      </Section>

      {/* Section: What they want */}
      <Section title="What they want">
        <div className="grid sm:grid-cols-2 gap-6">
          <Bars
            title="Top concerns"
            items={data.top_concerns.map((c) => ({ label: c.label, value: c.count }))}
            empty="No concerns selected yet."
          />
          <Bars
            title="Severity distribution (1 mild → 5 severe)"
            items={data.severity_distribution.map((s) => ({ label: `Severity ${s.severity}`, value: s.count }))}
            empty="No severity data yet."
          />
        </div>
      </Section>

      {/* Section: What they buy */}
      <Section title="What they buy (and what we recommend)">
        <div className="grid sm:grid-cols-2 gap-6">
          <Bars
            title="Top supplements recommended"
            items={data.top_supplements_recommended.map((s) => ({ label: s.name, value: s.count }))}
            empty="No assessments yet."
            testid="supplements-recommended"
          />
          <Bars
            title="Top supplements clicked (Buy)"
            items={data.top_supplements_clicked.map((s) => ({ label: s.name, value: s.count }))}
            empty="No affiliate clicks yet. This becomes your Phase-2 white-label signal."
            testid="supplements-clicked"
          />
        </div>
        <div className="grid sm:grid-cols-2 gap-6 mt-6">
          <Bars
            title="Retailers clicked"
            items={data.top_retailers.map((r) => ({ label: r.retailer, value: r.count }))}
            empty="—"
          />
          <Bars
            title="Regions clicked from"
            items={data.top_regions.map((r) => ({ label: r.region, value: r.count }))}
            empty="—"
          />
        </div>
      </Section>

      <p className="mt-12 text-xs" style={{ color: "var(--text-2)" }}>
        All data is aggregated. Generated {new Date(data.generated_at).toLocaleString()}.
      </p>
    </main>
  );
}

function Stat({ icon: Icon, label, value, delta }) {
  return (
    <div className="card-soft p-5">
      <Icon className="w-4 h-4 mb-2" strokeWidth={1.5} style={{ color: "var(--brand)" }} />
      <p className="label-eyebrow mb-1">{label}</p>
      <p className="font-serif text-3xl leading-none mb-1">{value}</p>
      <p className="text-xs" style={{ color: "var(--text-2)" }}>{delta}</p>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-12">
      <h2 className="font-serif text-2xl mb-5">{title}</h2>
      {children}
    </section>
  );
}

function Bars({ title, items, extra = [], empty, testid }) {
  const combined = [...items, ...extra];
  const max = Math.max(1, ...combined.map((i) => i.value));
  return (
    <div className="card-soft p-5" data-testid={testid}>
      <p className="label-eyebrow mb-3">{title}</p>
      {combined.length === 0 ? (
        <p className="text-sm" style={{ color: "var(--text-2)" }}>{empty}</p>
      ) : (
        <div className="space-y-2">
          {combined.map((it, i) => (
            <div key={`${it.label}-${i}`} className="flex items-center gap-3 text-sm">
              <span className="w-44 truncate" style={{ color: "var(--text-2)" }}>{it.label}</span>
              <div className="flex-1 rounded-full overflow-hidden h-2" style={{ background: "var(--bg-2)" }}>
                <div className="h-full rounded-full" style={{ width: `${(it.value / max) * 100}%`, background: "var(--brand)" }} />
              </div>
              <span className="w-8 text-right" style={{ color: "var(--text)" }}>{it.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stats({ rows }) {
  return (
    <div className="card-soft p-5">
      <p className="label-eyebrow mb-3">Engagement</p>
      <table className="w-full text-sm">
        <tbody>
          {rows.map(([label, value]) => (
            <tr key={label} className="border-b last:border-0" style={{ borderColor: "var(--border)" }}>
              <td className="py-2" style={{ color: "var(--text-2)" }}>{label}</td>
              <td className="py-2 text-right font-serif text-lg" style={{ color: "var(--text)" }}>{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SignupSparkline({ items }) {
  if (!items || items.length === 0) {
    return (
      <div className="card-soft p-5">
        <p className="label-eyebrow mb-3">Signups (last 90 days)</p>
        <p className="text-sm" style={{ color: "var(--text-2)" }}>No signups yet.</p>
      </div>
    );
  }
  const max = Math.max(1, ...items.map((i) => i.count));
  return (
    <div className="card-soft p-5">
      <p className="label-eyebrow mb-3">Signups (last 90 days)</p>
      <div className="flex items-end gap-0.5 h-24">
        {items.map((it) => (
          <div
            key={it.date}
            title={`${it.date}: ${it.count}`}
            className="flex-1 rounded-t"
            style={{ height: `${(it.count / max) * 100}%`, background: "var(--brand)", minHeight: "2px" }}
          />
        ))}
      </div>
      <div className="flex justify-between text-xs mt-2" style={{ color: "var(--text-2)" }}>
        <span>{items[0]?.date}</span>
        <span>{items[items.length - 1]?.date}</span>
      </div>
    </div>
  );
}
